/**
 * Som e vibração do desfecho.
 *
 * Na portaria o operador está com a fila na frente e o aparelho na mão, muitas
 * vezes sem olhar para a tela no instante da leitura. O som é o primeiro aviso;
 * a tela confirma depois. Três timbres bastam — entrou, pare e confira, recusou
 * — porque o operador precisa distinguir de ouvido, não catalogar.
 *
 * Os arquivos vivem em `assets/sons/`, os mesmos do app nativo.
 *
 * No navegador, dois limites que o app não tem:
 *
 * - Som só depois do primeiro toque ou tecla na página — regra de todo
 *   navegador contra site que grita sozinho. O login e o caixa já pedem toque,
 *   então na hora da primeira leitura o áudio está liberado.
 * - O iPhone não vibra: o Safari não implementa vibração. Lá a redundância
 *   fica em cor, ícone, palavra e som.
 */

import type {MotivoValidacao} from '../dominio/tipos';
import somAtencao from '../../assets/sons/bipe_atencao.wav';
import somOk from '../../assets/sons/bipe_ok.wav';
import somRecusa from '../../assets/sons/bipe_recusa.wav';

interface Retorno {
  som: keyof typeof SONS;
  /** Milissegundos, ou padrão [espera, vibra, espera, vibra...] como no Android. */
  vibracao: number | number[];
}

const SONS = {
  bipe_ok: somOk,
  bipe_recusa: somRecusa,
  bipe_atencao: somAtencao,
};

/**
 * Quatro assinaturas para onze desfechos.
 *
 * No mãos livres o operador não olha a tela, então o retorno tátil e sonoro é
 * o desfecho — mas ninguém decora onze padrões no meio do show. O som diz três
 * coisas: passa, não passa, olha a tela. A vibração separa uma quarta, o "já
 * validado", porque é de longe a recusa mais comum na portaria e a que o
 * operador precisa reconhecer sem tirar o aparelho da fila.
 *
 * A tela continua dizendo o motivo exato — isto aqui é o resumo audível.
 */
const ENTROU: Retorno = {som: 'bipe_ok', vibracao: 80};
const JA_ENTROU: Retorno = {som: 'bipe_recusa', vibracao: 300};
const RECUSOU: Retorno = {som: 'bipe_recusa', vibracao: [0, 250, 150, 250]};
const CONFIRA: Retorno = {som: 'bipe_atencao', vibracao: 600};

export function retornoDoDesfecho(motivo: MotivoValidacao): Retorno {
  if (motivo === 'VALIDADO') {
    return ENTROU;
  }
  if (motivo === 'AGUARDANDO_CONFIRMACAO_DADOS') {
    return CONFIRA;
  }
  if (motivo === 'JA_VALIDADO') {
    return JA_ENTROU;
  }
  return RECUSOU;
}

/**
 * O padrão do Android começa esperando; o do navegador começa vibrando. Sem a
 * conversão, `[0, 250, 150, 250]` — duas vibradas de 250 — viraria uma só de
 * 150, e a recusa ficaria parecida com o aprovado na mão do operador.
 */
export function padraoDoNavegador(vibracao: number | number[]): number | number[] {
  if (!Array.isArray(vibracao)) {
    return vibracao;
  }
  const [espera, ...resto] = vibracao;
  return espera === 0 ? resto : [0, espera, ...resto];
}

/**
 * Áudio decodificado de antemão. Com `new Audio()` a cada leitura, o bipe
 * atrasa o bastante para o operador já ter olhado a tela — e aí o som não
 * serve de primeiro aviso.
 */
let contexto: AudioContext | null = null;
const buffers = new Map<string, AudioBuffer>();

async function carregarSons(ctx: AudioContext): Promise<void> {
  await Promise.all(
    Object.entries(SONS).map(async ([nome, url]) => {
      const bruto = await (await fetch(url)).arrayBuffer();
      buffers.set(nome, await ctx.decodeAudioData(bruto));
    }),
  );
}

/**
 * Libera o áudio no primeiro gesto. Fica escutando para sempre porque o iOS
 * volta a suspender o contexto quando o app sai da frente.
 */
function liberarAudio(): void {
  const Contexto = window.AudioContext ?? (window as never as {webkitAudioContext?: typeof AudioContext}).webkitAudioContext;
  if (!Contexto) {
    return;
  }
  if (!contexto) {
    contexto = new Contexto();
    carregarSons(contexto).catch(e => console.warn(`[retorno] sons não carregaram: ${e}`));
  }
  if (contexto.state !== 'running') {
    contexto.resume().catch(() => {});
  }
}

if (typeof window !== 'undefined') {
  window.addEventListener('pointerdown', liberarAudio, {capture: true});
  window.addEventListener('keydown', liberarAudio, {capture: true});
}

function tocar(som: string): void {
  const buffer = buffers.get(som);
  if (!contexto || !buffer) {
    // Primeira leitura antes de os sons decodificarem: toca pelo caminho lento
    // em vez de ficar mudo.
    new Audio(SONS[som as keyof typeof SONS]).play().catch(() => {});
    return;
  }
  const fonte = contexto.createBufferSource();
  fonte.buffer = buffer;
  fonte.connect(contexto.destination);
  fonte.start();
}

/**
 * Toca e vibra. Nunca lança: aparelho no silencioso, sem alto-falante ou sem
 * motor de vibração é caso normal de campo, e nada disso pode derrubar a tela
 * que acabou de decidir se a pessoa entra.
 */
export function avisar(motivo: MotivoValidacao): void {
  const {som, vibracao} = retornoDoDesfecho(motivo);

  try {
    tocar(som);
  } catch (e) {
    console.warn(`[retorno] som "${som}" não tocou: ${e}`);
  }

  try {
    navigator.vibrate?.(padraoDoNavegador(vibracao));
  } catch (e) {
    console.warn(`[retorno] vibração falhou: ${e}`);
  }
}
